// src/components/dashboard/DashboardGrid.jsx
// Drag/resize wrapper for dashboard chart cards (react-grid-layout, single 12-col
// space, no breakpoints). Always controlled: pass useDashboardLayout `layouts` +
// `onLayoutChange`. Hidden ids ride on layout entries (`hidden: true`).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { GridLayout, useContainerWidth } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { Eye, GripVertical, Maximize2, X } from 'lucide-react';
import { CARD_MIN } from './defaultLayouts';

const COLS = 12;
const FALLBACK_MIN = { minW: 2, minH: 2 };

const cardMin = (id) => CARD_MIN[id] || FALLBACK_MIN;

const clampEntry = (id, preset) => {
  const { minW, minH } = cardMin(id);
  const w = Math.max(minW, Math.min(Number.isFinite(preset.w) ? preset.w : 6, COLS));
  return {
    i: id, w, h: Math.max(minH, Number.isFinite(preset.h) ? preset.h : 6),
    x: Math.max(0, Math.min(Number.isFinite(preset.x) ? preset.x : 0, COLS - w)),
    y: Number.isFinite(preset.y) ? preset.y : 0,
    minW, minH, maxW: COLS,
    ...(preset.hidden === true ? { hidden: true } : {})
  };
};

// Merge stored layout with current items so late-arriving conditional cards join
// without losing user moves; entries for removed ids are dropped.
const mergeLayout = (stored, items, defaultLayout) => {
  const byId = {};
  (Array.isArray(stored) ? stored : []).forEach((e) => {
    if (e && typeof e.i === 'string') {
      byId[e.i] = e;
    }
  });
  return items.map((item, index) => {
    if (byId[item.id]) {
      return clampEntry(item.id, byId[item.id]);
    }
    const preset = defaultLayout[item.id] || {};
    return clampEntry(item.id, {
      ...preset,
      x: Number.isFinite(preset.x) ? preset.x : (index % 2) * 6,
      y: Number.isFinite(preset.y) ? preset.y : Math.floor(index / 2) * 6
    });
  });
};

// ECharts follows container size, but an explicit resize event covers edge cases mid-drag/resize.
const dispatchResize = () => window.dispatchEvent(new Event('resize'));

const DashboardGrid = ({ items, defaultLayout = {}, editMode = false, rowHeight = 40, layouts = [], onLayoutChange = () => {}, onToggleHidden = null }) => {
  const { t } = useTranslation();
  const { width, containerRef } = useContainerWidth();
  const hiddenSet = useMemo(() => {
    const set = new Set();
    (Array.isArray(layouts) ? layouts : []).forEach((e) => {
      if (e && e.hidden === true) {
        set.add(e.i);
      }
    });
    return set;
  }, [layouts]);
  const visibleItems = useMemo(() => items.filter((item) => !hiddenSet.has(item.id)), [items, hiddenSet]);
  const activeLayout = useMemo(() => mergeLayout(layouts, visibleItems, defaultLayout), [layouts, visibleItems, defaultLayout]);
  const [fullscreenId, setFullscreenId] = useState(null);
  const fullscreenItem = items.find((item) => item.id === fullscreenId) || null;
  const modalContentRef = useRef(null);

  useEffect(() => {
    if (!fullscreenItem) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setFullscreenId(null);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (modalContentRef.current) {
      modalContentRef.current.focus();
    }
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [fullscreenItem]);

  // Width is 0/undefined before the container is measured — wait for it.
  if (!width) {
    return <div ref={containerRef} style={{ minHeight: rowHeight * 6 }} />;
  }

  return (
    <div ref={containerRef}>
      <GridLayout className="layout" width={width} layout={activeLayout} cols={COLS} rowHeight={rowHeight}
        compactType={editMode ? 'vertical' : null} margin={[8, 8]} containerPadding={[0, 0]}
        draggableHandle=".drag-handle" isDraggable={editMode} isResizable={editMode}
        onLayoutChange={onLayoutChange} onResizeStop={dispatchResize} onDragStop={dispatchResize}>
        {visibleItems.map((item) => (
          <div key={item.id} className="relative h-full flex flex-col">
            <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
              <button type="button" onClick={() => setFullscreenId(item.id)} title={t('dashboard.layout.expand', 'Expand')}
                aria-label={`${t('dashboard.layout.expand', 'Expand')}: ${item.fullscreenTitle || item.id}`}
                className="flex items-center px-2 py-1 rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200">
                <Maximize2 size={14} />
              </button>
              {editMode && typeof onToggleHidden === 'function' && (
                <button type="button" onClick={() => onToggleHidden(item.id)} title={t('dashboard.layout.hide', 'Hide')}
                  aria-label={`${t('dashboard.layout.hide', 'Hide')}: ${item.fullscreenTitle || item.id}`}
                  className="flex items-center px-2 py-1 rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200">
                  <Eye size={14} />
                </button>
              )}
              {editMode && (
                <div className="drag-handle flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 text-gray-500 cursor-move hover:bg-gray-200" title="Drag to move">
                  <GripVertical size={14} />
                </div>
              )}
            </div>
            {item.element}
          </div>
        ))}
      </GridLayout>
      {fullscreenItem && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={fullscreenItem.fullscreenTitle || fullscreenItem.id}>
          <div className="absolute inset-0 bg-black/60" onClick={() => setFullscreenId(null)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-auto p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800">{fullscreenItem.fullscreenTitle || fullscreenItem.id}</h3>
              <button type="button" onClick={() => setFullscreenId(null)} aria-label={t('dashboard.layout.close', 'Close')}
                className="flex items-center px-2 py-1 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200">
                <X size={16} />
              </button>
            </div>
            <div ref={modalContentRef} tabIndex={-1}>{fullscreenItem.fullscreenContent || fullscreenItem.element}</div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

DashboardGrid.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    element: PropTypes.node.isRequired,
    fullscreenTitle: PropTypes.string,
    fullscreenContent: PropTypes.node
  })).isRequired,
  defaultLayout: PropTypes.object,
  editMode: PropTypes.bool,
  rowHeight: PropTypes.number,
  layouts: PropTypes.array,
  onLayoutChange: PropTypes.func,
  onToggleHidden: PropTypes.func
};

export default DashboardGrid;
